import { useMemo } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";

import { WithTranslator } from "Components/Generic/Translator";
import { isIndexInRange } from "Utils/Array";
import { isNotNullOrWhiteSpace } from "Utils/String";
import { WithTheme } from "../Theme/WithTheme";
import { IWizardProps } from "./IWizardProps";
import { useWizardStyles } from "./UseWizardStyles";

export const Wizard = WithTheme(
  WithTranslator((props: IWizardProps): JSX.Element => {
    const {
      selectedPage,
      styleSet,
      pages,
      translator,
      onPreviousPage,
      previousButtonText,
      onNextPage,
      nextButtonText,
      onFinish,
      finishButtonText,
      useActions,
      theme: { spacing }
    } = props;

    const isLastPage = selectedPage === (pages?.length ?? 0) - 1;
    const page = isIndexInRange(pages, selectedPage) ? pages[selectedPage] : null;
    const styles = useWizardStyles(styleSet);

    const actions = useMemo(
      () => (
        <Stack
          horizontal
          disableShrink
          horizontalAlign="end"
          tokens={{ childrenGap: spacing.s2 }}
          className={styles.actions}
        >
          <Stack.Item>
            <DefaultButton onClick={onPreviousPage} disabled={onPreviousPage == null}>
              {isNotNullOrWhiteSpace(previousButtonText)
                ? previousButtonText
                : translator.get(`generic.previous`, `Previous`)}
            </DefaultButton>
          </Stack.Item>
          {!isLastPage
            ? (
              <Stack.Item>
                <PrimaryButton onClick={onNextPage} disabled={onNextPage == null}>
                  {isNotNullOrWhiteSpace(nextButtonText) ? nextButtonText : translator.get(`generic.next`, `Next`)}
                </PrimaryButton>
              </Stack.Item>
            )
            : null}
          {isLastPage
            ? (
              <Stack.Item>
                <PrimaryButton onClick={onFinish} disabled={!isLastPage}>
                  {isNotNullOrWhiteSpace(finishButtonText)
                    ? finishButtonText
                    : translator.get(`generic.finish`, `Finish`)}
                </PrimaryButton>
              </Stack.Item>
            )
            : null}
        </Stack>
      ),
      [
        finishButtonText,
        isLastPage,
        nextButtonText,
        onFinish,
        onNextPage,
        onPreviousPage,
        previousButtonText,
        spacing,
        translator,
        styles
      ]
    );

    return (
      <Stack tokens={{ childrenGap: spacing.m }} className={styles.root} verticalAlign="space-between">
        <Stack horizontal disableShrink className={styles.header} horizontalAlign="space-between">
          {pages.map((p, index) => {
            const headerPageClassName = p.disabled ?? false ? styles.headerPageDisabled : ``;
            const badgeClassNames = [styles.headerBadge, index === selectedPage ? styles.headerBadgeActive : ``];

            return (
              <Stack
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                grow
                horizontal
                tokens={{ childrenGap: spacing.s1 }}
                disableShrink
                horizontalAlign="center"
                className={headerPageClassName}
              >
                <Stack.Item className={badgeClassNames.join(` `)}>{index + 1}</Stack.Item>
                <Stack.Item className={styles.headerText}>
                  <Text>{p.header}</Text>
                </Stack.Item>
              </Stack>
            );
          })}
        </Stack>
        <Stack.Item grow className={styles.content}>
          {page?.contentFn?.()}
        </Stack.Item>
        {useActions !== false ? actions : null}
      </Stack>
    );
  })
);
